import { designVariables } from "@studio3t/design-variables";
import {
  BadgeInfoSubtle,
  ButtonMediumSecondaryIcon,
  ButtonMediumSecondaryLeadingIcon,
  GenericInput,
  Icon,
  IconSVG,
  InputType,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { workspacesApi } from "@/api/client";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { ListPageHeader } from "@/components/ListPageHeader";
import { OverlayScroll } from "@/components/OverlayScroll";
import { FilterControls, FilterMultiSelect } from "@/components/TableFilters";
import { queryKeys, useJobs } from "@/hooks/useApi";
import { useImportJob } from "@/hooks/useImportJob";
import { usePersistedState } from "@/hooks/usePersistedState";
import type { OpenPipelineItem } from "@/pages/NewStudio/openPipelineList";
import { useLabeledButton } from "@/pages/NewStudio/PipelineNavBar";
import type { JobMetadata } from "@/types/api";
import { parseAllowedList } from "@/utils/storedValue";
import { studioPath, studioWorkspacePath } from "@/utils/studioRoute";
import {
  type PipelineStatusFilter,
  STATUS_FILTER_OPTIONS,
  visiblePipelineItems,
} from "./createPipelineItems";

const SEMANTIC = designVariables.ui.semantic;
const CARDS = designVariables.ui.cards;
const LIST = designVariables.ui.list;
const TYPE = designVariables.type;
// The rows' wash is neutrals/10, which the theme object doesn't expose.
const GREY_BACKGROUND = designVariables.primitives.neutrals["10"];

// "Add pipeline" lands here (Figma Bridge_OpenStudio 14899-267045): open
// an existing pipeline in the Studio — every deployed job and saved draft,
// filtered by name and status — or start from a clean slate.

function useWorkspaceSummaries() {
  return useQuery({
    queryKey: [...queryKeys.workspaces, "summaries"],
    queryFn: () => workspacesApi.list(),
  });
}

export default function CreatePipeline() {
  const navigate = useNavigate();
  return (
    <PageRoot>
      <ListPageHeader title="Create pipeline" />
      <Centered>
        <Card data-testid="create-pipeline">
          <Intro>Open an existing pipeline or start fresh</Intro>
          <PipelinePicker />
          <Section>
            <SectionTitle>Clean slate</SectionTitle>
            <CleanSlate>
              <ButtonMediumSecondaryLeadingIcon
                text="Start fresh"
                icon={Icon.Plus}
                onClick={() => navigate(studioPath())}
                testId="start-fresh"
              />
            </CleanSlate>
          </Section>
        </Card>
      </Centered>
    </PageRoot>
  );
}

// The filters are remembered per user like the pipelines list's
// (TTTL-186); the unfiltered defaults are stored as absence.
const STORAGE_KEYS = {
  name: "create-pipeline:name",
  statuses: "create-pipeline:statuses",
} as const;
const STATUS_VALUES = STATUS_FILTER_OPTIONS.map((o) => o.value);

function usePersistedFilters() {
  const [name, setName] = usePersistedState<string>(
    STORAGE_KEYS.name,
    (raw) => raw ?? "",
    serializeName,
  );
  const [statuses, setStatuses] = usePersistedState<PipelineStatusFilter[]>(
    STORAGE_KEYS.statuses,
    (raw) => parseAllowedList(raw, STATUS_VALUES),
    serializeStatuses,
  );
  return { name, setName, statuses, setStatuses };
}

function serializeName(name: string): string | null {
  return name || null;
}

function serializeStatuses(statuses: PipelineStatusFilter[]): string | null {
  return statuses.length ? JSON.stringify(statuses) : null;
}

// The "Pipelines" section: name + status filters over the list of rows.
function PipelinePicker() {
  const navigate = useNavigate();
  const { data: jobs } = useJobs();
  const { data: workspaces } = useWorkspaceSummaries();
  const { importJob, loading: importing } = useImportJob();
  const { name, setName, statuses, setStatuses } = usePersistedFilters();

  const items = visiblePipelineItems(workspaces ?? [], jobs ?? [], {
    name,
    statuses,
  });
  const jobsByName = new Map((jobs ?? []).map((job) => [job.name, job]));
  const open = (item: OpenPipelineItem) => {
    if (item.kind === "draft") navigate(studioWorkspacePath(item.workspaceId));
    else void importJob(jobsByName.get(item.name) as JobMetadata);
  };

  return (
    <Section>
      <SectionTitle>Pipelines</SectionTitle>
      <FilterRow>
        <NameFilter>
          <GenericInput
            type={InputType.Text}
            value={name}
            placeholder="Filter by name"
            onChange={(e) => setName(e.target.value)}
            allowClear={name.length > 0}
            onClear={() => setName("")}
          />
        </NameFilter>
        {/* The dropdown's root is a stretching block; the controls' inner
            flex sizes it to its content, as the tables' filter bars do. */}
        <FilterControls>
          <FilterMultiSelect
            placeholder="Status"
            values={statuses}
            options={STATUS_FILTER_OPTIONS}
            onChange={setStatuses}
          />
        </FilterControls>
      </FilterRow>
      <Rows>
        <RowList data-testid="create-pipeline-list">
          {items.map((item) => (
            <PipelineRow
              // Two drafts can share a name; the workspace id tells them apart.
              key={
                item.kind === "draft" ? item.workspaceId : `job:${item.name}`
              }
              item={item}
              busy={importing}
              onOpen={() => open(item)}
            />
          ))}
          {items.length === 0 && <Empty>No pipelines match your filters</Empty>}
        </RowList>
      </Rows>
    </Section>
  );
}

// One pipeline: trace icon, name, its status badge and the arrow that
// opens it in the Studio — viewing a running one, editing the rest.
function PipelineRow({
  item,
  busy,
  onOpen,
}: {
  item: OpenPipelineItem;
  busy: boolean;
  onOpen: () => void;
}) {
  const running = item.kind === "job" && item.state === "Running";
  // The lib's buttons don't forward aria-label; the icon-only arrow gets
  // its name through the node, like the nav bar's buttons.
  const labelRef = useLabeledButton(
    `${running ? "View" : "Edit"} ${item.name}`,
  );
  return (
    <Row data-testid={`create-pipeline-row-${item.name}`}>
      <RowItem>
        <IconSVG
          icon={Icon.Trace}
          size={18}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
        <RowName>{item.name}</RowName>
        {item.kind === "draft" ? (
          <BadgeInfoSubtle>draft</BadgeInfoSubtle>
        ) : (
          <JobStatusBadge state={item.state} />
        )}
      </RowItem>
      <ButtonMediumSecondaryIcon
        ref={labelRef}
        icon={Icon.Right}
        disabled={busy}
        onClick={onOpen}
        testId="open-pipeline"
      />
    </Row>
  );
}

// Content area: 10px top, 18px sides/bottom, the card centred.
const PageRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  min-height: 100%;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: ${SEMANTIC.spacingl}px;
`;

// Bridge_OpenStudio: 600px card, 40px padding and gaps, card1 chrome.
const Card = styled.div`
  width: 600px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 40px;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: ${CARDS.corner}px;
`;

const Intro = styled.p`
  margin: 0;
  font-size: ${TYPE.paragraph.large.regular.fontSize}px;
  font-weight: ${TYPE.paragraph.large.regular.fontWeight};
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextBody};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Form/header: H5 title with a hairline running to the right edge.
const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  margin: 0;
  height: 20px;
  font-size: ${TYPE.heading.h5.fontSize}px;
  font-weight: ${TYPE.heading.h5.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(p) => p.theme.semanticBorderLight};
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
`;

const NameFilter = styled.div`
  flex: 1;
  min-width: 0;
`;

// Six rows show; the rest scroll behind the app's themed scrollbars. The
// flex column lives on the inner list: OverlayScrollbars puts its own
// viewport between the host and the rows.
const Rows = styled(OverlayScroll)`
  max-height: 410px;
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacings}px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  padding: ${SEMANTIC.spacings}px ${SEMANTIC.spacingm}px ${SEMANTIC.spacings}px
    ${SEMANTIC.spacings}px;
  background: ${GREY_BACKGROUND};
  border: 1px solid ${(p) => p.theme.semanticBorderLight};
  border-radius: ${CARDS.corner}px;
`;

// List/item: 40px tall, 12px side padding, 6px gap; the badge trails the
// name.
const RowItem = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${LIST.item.gap}px;
  height: 40px;
  padding: 0 ${LIST.item.paddingw}px;
`;

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${TYPE.singleLine.default.medium.fontSize}px;
  font-weight: ${TYPE.singleLine.default.medium.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextBody};
`;

const Empty = styled.p`
  margin: 0;
  padding: ${SEMANTIC.spacings}px 0;
  font-size: ${TYPE.singleLine.small.regular.fontSize}px;
  font-style: italic;
  color: ${(p) => p.theme.semanticTextLight};
`;

const CleanSlate = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: ${GREY_BACKGROUND};
  border: 1px solid ${(p) => p.theme.semanticBorderLight};
  border-radius: ${CARDS.corner}px;
`;
